// Private types ---------------------------------------------------------------

type Operation = () => void | Promise<void>

// Public types ----------------------------------------------------------------

export type ReportSession = Readonly<{
  signal: AbortSignal
  start: (initialize: Operation) => void
  enqueue: (operation: Operation) => Promise<void>
  wait: <T>(operation: Promise<T>) => Promise<T>
  onCleanup: (cleanup: Operation) => void
  destroy: () => void
}>

// Public functions ------------------------------------------------------------

// Orders report updates behind initialization and releases resources after work stops.
export const createReportSession = (
  onError: (error: unknown) => void
): ReportSession => {
  const controller = new AbortController()
  const { signal } = controller
  let pending = Promise.resolve()
  let cleanups: readonly Operation[] = []

  // Ignore cancellation while reporting failures of active report operations.
  const reportError = (error: unknown): void => {
    if (!signal.aborted) {
      onError(error)
    }
  }

  // Release dependent resources in reverse acquisition order, even after a failure.
  const release = async (): Promise<void> => {
    for (const cleanup of [...cleanups].reverse()) {
      try {
        await cleanup()
      } catch (error) {
        onError(error)
      }
    }

    cleanups = []
  }

  // Stop fetches and listeners immediately; let in-flight library calls settle first.
  const destroy = (): void => {
    if (signal.aborted) {
      return
    }

    controller.abort()
    pending.then(release)
  }

  // Prevent a completed library call from resuming work on a destroyed report.
  const wait = async <T>(operation: Promise<T>): Promise<T> => {
    const result = await operation
    signal.throwIfAborted()

    return result
  }

  // Keep server updates and exports in the order they arrived.
  const enqueue = (operation: Operation): Promise<void> => {
    pending = pending
      .then(() => {
        signal.throwIfAborted()

        return operation()
      })
      .catch(reportError)

    return pending
  }

  // A failed mount cannot accept later updates against a partially loaded viewer.
  const start = (initialize: Operation): void => {
    enqueue(async () => {
      try {
        await initialize()
      } catch (error) {
        reportError(error)
        destroy()
      }
    })
  }

  // Register resources before checking cancellation so late acquisitions are released.
  const onCleanup = (cleanup: Operation): void => {
    cleanups = [...cleanups, cleanup]
  }

  return { signal, start, enqueue, wait, onCleanup, destroy }
}
